class LooksCarousel extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.startX = 0;
    this.scrollStart = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.items = [...this.querySelectorAll('[data-carousel-item]')];
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');

    const section = this.closest('.looks-showcase');
    this.featuredContainer = section?.querySelector('[data-featured-look]');
    this.shouldUpdateUrl = window.location.pathname.indexOf('/pages/look') === 0;

    if (!this.track || this.items.length === 0) return;

    this.setupDrag();
    this.setupArrows();
    this.setupInlineSwap(section);
  }

  setupDrag() {
    this.track.addEventListener('touchstart', (e) => {
      this.isDragging = true;
      this.startX = e.touches[0].pageX;
      this.scrollStart = this.track.scrollLeft;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      if (!this.isDragging) return;
      const x = e.touches[0].pageX;
      const diff = this.startX - x;
      this.track.scrollLeft = this.scrollStart + diff;
    }, { passive: true });

    this.track.addEventListener('touchend', () => {
      this.isDragging = false;
    });
  }

  setupArrows() {
    if (!this.prevBtn || !this.nextBtn) return;

    const scrollAmount = () => {
      const item = this.items[0];
      if (!item) return 400;
      return item.offsetWidth + 8;
    };

    this.prevBtn.addEventListener('click', () => {
      this.track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
    });

    this.nextBtn.addEventListener('click', () => {
      this.track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
    });

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.prevBtn.style.display = scrollLeft <= 8 ? 'none' : '';
      this.nextBtn.style.display = scrollLeft + clientWidth >= scrollWidth - 8 ? 'none' : '';
    };

    this.track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
  }

  setupInlineSwap(section) {
    if (!this.featuredContainer || !section) return;

    // Use capture phase to intercept clicks before view transitions can navigate
    this.addEventListener('click', (e) => {
      const item = e.target.closest('[data-carousel-item]');
      if (!item) return;

      // Don't swap if this look is already active
      if (item.classList.contains('looks-showcase__item--active')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const handle = item.dataset.lookHandle;
      const tpl = section.querySelector(`[data-look-template="${CSS.escape(handle)}"]`);

      // Only intercept if we have a template to swap in
      if (!tpl) return;

      e.preventDefault();
      e.stopPropagation();

      // Get the look URL from the item's href for URL updating
      const lookUrl = item.getAttribute('href');

      this.swapLook(tpl, handle, lookUrl);
    }, { capture: true });
  }

  swapLook(tpl, handle, lookUrl) {
    this.featuredContainer.classList.add('look-page--transitioning-out');

    setTimeout(() => {
      // Clear and insert cloned template content
      this.featuredContainer.innerHTML = '';
      this.featuredContainer.appendChild(tpl.content.cloneNode(true));

      // Re-initialize cart modal web components in the new content
      this.featuredContainer.querySelectorAll('look-cart-modal').forEach((el) => {
        if (typeof el.connectedCallback === 'function') {
          el.connectedCallback();
        }
      });

      this.featuredContainer.classList.remove('look-page--transitioning-out');
      this.featuredContainer.classList.add('look-page--transitioning-in');
      setTimeout(() => {
        this.featuredContainer.classList.remove('look-page--transitioning-in');
      }, 400);

      // Update active state on carousel items
      this.items.forEach((i) => i.classList.remove('looks-showcase__item--active'));
      if (handle) {
        const activeItem = this.track.querySelector(`[data-look-handle="${CSS.escape(handle)}"]`);
        if (activeItem) activeItem.classList.add('looks-showcase__item--active');
      }

      // Update URL to the look's page
      if (this.shouldUpdateUrl && lookUrl) {
        history.pushState({ lookHandle: handle }, '', lookUrl);
      }

      // Scroll to the very top of the featured section, accounting for sticky header
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '80', 10);
      const top = this.featuredContainer.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 300);
  }
}

if (!customElements.get('looks-carousel')) {
  customElements.define('looks-carousel', LooksCarousel);
}
